import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { useEffect, useMemo } from 'react';

import { apiClient } from '../services/api';
import { enUS, heIL } from '@clerk/localizations';
import { useLanguage } from '../contexts/LanguageContext';
import { SupportedLanguages } from '@/i18n/i18next';
import { PosthogProvider } from './PostHogProvider';

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Publishable Key');
}

const clerkLocalizations = {
  en: enUS,
  he: heIL,
} as const satisfies Record<SupportedLanguages, unknown>;

const ApiProvider = ({ children }: { children: React.ReactNode }) => {
  const { getToken } = useAuth();

  useEffect(() => {
    const interceptor = apiClient.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    return () => {
      apiClient.interceptors.request.eject(interceptor);
    };
  }, [getToken]);

  return children;
};

const ClerkProviderWithLanguage = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { language } = useLanguage();

  const currentLocalization = useMemo(
    () => clerkLocalizations[language],
    [language]
  );

  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      localization={currentLocalization}
    >
      <PosthogProvider>
        <ApiProvider>{children}</ApiProvider>
      </PosthogProvider>
    </ClerkProvider>
  );
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  return <ClerkProviderWithLanguage>{children}</ClerkProviderWithLanguage>;
};
